<template>
  <div class="max-w-7xl mx-auto mt-8 bg-white p-6 rounded-xl shadow-md">
    <!-- Header -->
    <div class="mb-6">
      <h2 class="text-2xl font-bold text-gray-800 mb-2 flex items-center gap-2">
        AI Feedback Generation
        <BaseInfoIcon :text="ERKLAERUNG.kiFeedback" label="AI Feedback Generation" />
      </h2>
      <p class="text-sm text-gray-500">Generate AI-powered evaluations for all users in a session</p>
      <div class="mt-2 flex flex-wrap items-center gap-2">
        <span
          v-if="currentLlmModel"
          class="inline-flex items-center px-2 py-1 text-xs font-semibold rounded-full bg-gray-100 text-gray-700"
        >
          Model: {{ currentLlmModel }}
        </span>
        <span
          v-if="estimatedTimePerUserLlm"
          class="inline-flex items-center px-2 py-1 text-xs font-semibold rounded-full bg-indigo-100 text-indigo-700"
        >
          LLM est.: {{ estimatedTimePerUserLlm }} / user
        </span>
        <span
          v-if="estimatedTimePerUserRag"
          class="inline-flex items-center px-2 py-1 text-xs font-semibold rounded-full bg-purple-100 text-purple-700"
        >
          RAG est.: {{ estimatedTimePerUserRag }} / user
        </span>
      </div>
    </div>

    <!-- Session Selection -->
    <div class="mb-6 flex items-end gap-4">
      <div class="w-full md:w-96">
        <label class="block mb-2 font-semibold text-gray-700">Select Session</label>
        <BaseSelect
          v-model="selectedSessionId"
          :options="sessionOptions"
          placeholder="-- Select a session --"
          :showPlaceholder="true"
        />
      </div>
      
      <BaseButton 
        v-if="selectedSessionId"
        variant="primary" 
        @click="generateBulkFeedback(false)"
        :disabled="isGenerating"
      >
        <span v-if="!isGenerating">Generate for All Users (LLM)</span>
        <span v-else-if="!isGeneratingForDifferentSession" class="flex items-center">
          <ArrowPathIcon class="animate-spin h-5 w-5 mr-2" />
          Generating...
        </span>
        <span v-else class="flex items-center">
          <ArrowPathIcon class="animate-spin h-5 w-5 mr-2" />
          Generating for Session {{ generatingForSessionId }}...
        </span>
      </BaseButton>
      <BaseInfoIcon
        v-if="selectedSessionId"
        :text="ERKLAERUNG.kiFeedbackLlm"
        label="Generate for All Users (LLM)"
      />

      <BaseButton 
        v-if="selectedSessionId"
        variant="secondary" 
        @click="generateBulkFeedback(true)"
        :disabled="isGenerating"
        tooltip="Uses similar past evaluations as context"
      >
        <span v-if="!isGenerating">Generate with RAG</span>
        <span v-else-if="!isGeneratingForDifferentSession" class="flex items-center">
          <ArrowPathIcon class="animate-spin h-5 w-5 mr-2" />
          Generating...
        </span>
        <span v-else class="flex items-center">
          <ArrowPathIcon class="animate-spin h-5 w-5 mr-2" />
          Generating for Session {{ generatingForSessionId }}...
        </span>
      </BaseButton>
      <BaseInfoIcon
        v-if="selectedSessionId"
        :text="ERKLAERUNG.kiFeedbackRag"
        label="Generate with RAG"
      />
    </div>

    <!-- Progress Indicator - Only show when generating for current session -->
    <div v-if="isGenerating && !isGeneratingForDifferentSession" class="mb-6 p-4 rounded-lg bg-indigo-50 border border-indigo-200">
      <div class="flex items-center justify-between mb-2">
        <h3 class="font-semibold text-indigo-900">Generating AI Feedback...</h3>
        <span class="text-sm text-indigo-700 font-medium">
          {{ feedbackResults.length }} / {{ totalUsersToProcess }} users processed
        </span>
      </div>
      <div class="w-full bg-indigo-200 rounded-full h-2.5">
        <div 
          class="bg-indigo-600 h-2.5 rounded-full transition-all duration-300"
          :style="{ width: totalUsersToProcess > 0 ? (feedbackResults.length / totalUsersToProcess * 100) + '%' : '0%' }"
        ></div>
      </div>
      <div class="flex items-center justify-between mt-2">
        <p class="text-xs text-indigo-600">
          This may take several minutes depending on the number of users...
        </p>
        <p v-if="estimatedTimeRemaining" class="text-xs text-indigo-700 font-semibold">
          Estimated time remaining: {{ estimatedTimeRemaining }}
        </p>
      </div>
    </div>

    <!-- Generation Summary -->
    <div v-if="generationResult" class="mb-6 p-4 rounded-lg bg-blue-50 border border-blue-200">
      <div class="flex items-center justify-between">
        <div>
          <h3 class="font-semibold text-blue-900">Generation Complete</h3>
          <p class="text-sm text-blue-700 mt-1">
            Total: {{ generationResult.total_users }} | 
            Success: <span class="font-semibold text-green-600">{{ generationResult.successful }}</span> | 
            Failed: <span class="font-semibold text-red-600">{{ generationResult.failed }}</span> |
            Pipeline: <span class="font-semibold">{{ generationResult.pipeline_used }}</span>
            <span v-if="generationResult.model_used || currentLlmModel">
              | Model: <span class="font-semibold">{{ generationResult.model_used || currentLlmModel }}</span>
            </span>
          </p>
        </div>
        <div class="flex gap-2">
          <BaseButton variant="secondary" size="sm" @click="approveAll">
            Approve All
          </BaseButton>
          <BaseButton variant="cancel" size="sm" @click="declineAll">
            Decline All
          </BaseButton>
          <BaseButton variant="delete" size="sm" @click="clearAllSuggestions">
            Clear All
          </BaseButton>
        </div>
      </div>
    </div>

    <!-- Results List -->
    <div v-if="feedbackResults.length" class="space-y-4">
      <div 
        v-for="(result, index) in feedbackResults" 
        :key="result.user_id"
        class="border rounded-lg p-4 hover:shadow-md transition-shadow"
        :class="{
          'bg-gray-50 border-gray-300': result.approved === null,
          'bg-green-50 border-green-300': result.approved === true,
          'bg-red-50 border-red-300': result.approved === false,
          'bg-red-100 border-red-400': result.status === 'error'
        }"
      >
        <!-- User Header -->
        <div class="flex items-start justify-between mb-3">
          <div>
            <h3 class="text-lg font-semibold text-gray-800">{{ result.user_name }}</h3>
            <div class="flex gap-2 mt-1">
              <span v-if="result.role_name" class="px-2 py-1 bg-indigo-100 text-indigo-800 rounded text-xs font-semibold">
                {{ result.role_name }}
              </span>
              <span v-if="result.team_name" class="px-2 py-1 bg-green-100 text-green-800 rounded text-xs font-semibold">
                {{ result.team_name }}
              </span>
              <span v-if="result.status === 'error'" class="px-2 py-1 bg-red-200 text-red-900 rounded text-xs font-semibold">
                Error
              </span>
            </div>
          </div>
          
          <!-- Action Buttons -->
          <div v-if="result.status !== 'error'" class="flex gap-2">
            <BaseButton 
              v-if="!result.isEditing"
              variant="edit" 
              size="sm" 
              @click="toggleEdit(index)"
              :disabled="result.isProcessing"
            >
              Edit
            </BaseButton>
            <BaseButton 
              v-if="result.isEditing"
              variant="primary" 
              size="sm" 
              @click="saveEdit(index)"
              :disabled="result.isProcessing"
            >
              Save
            </BaseButton>
            <BaseButton 
              v-if="result.isEditing"
              variant="cancel" 
              size="sm" 
              @click="cancelEdit(index)"
              :disabled="result.isProcessing"
            >
              Cancel
            </BaseButton>
            <BaseButton 
              v-if="result.approved !== true && !result.isEditing"
              variant="primary" 
              size="sm" 
              @click="approveFeedback(index)"
              :disabled="result.isProcessing"
            >
              <span v-if="!result.isProcessing">Approve</span>
              <span v-else>Processing...</span>
            </BaseButton>
            <BaseButton 
              v-if="result.approved !== false && !result.isEditing"
              variant="cancel" 
              size="sm" 
              @click="declineFeedback(index)"
              :disabled="result.isProcessing"
            >
              <span v-if="!result.isProcessing">Decline</span>
              <span v-else>Processing...</span>
            </BaseButton>
            <BaseButton 
              v-if="!result.isEditing"
              variant="delete" 
              size="sm" 
              @click="clearIndividualSuggestion(index)"
              :disabled="result.isProcessing"
            >
              Clear
            </BaseButton>
          </div>
        </div>

        <!-- Error Message -->
        <div v-if="result.status === 'error'" class="mb-3 p-3 bg-red-100 border border-red-300 rounded">
          <p class="text-sm text-red-800 font-semibold">Error:</p>
          <p class="text-sm text-red-700">{{ result.error_message }}</p>
        </div>

        <!-- Input Data Section (Expandable) -->
        <div v-if="result.status !== 'error' && (result.user_history || result.team_history)" class="mb-3">
          <button
            @click="result.showInputData = !result.showInputData"
            class="flex items-center justify-between w-full p-2 bg-gray-100 hover:bg-gray-200 rounded text-sm font-semibold text-gray-700 transition-colors"
          >
            <span class="flex items-center">
              <Squares2X2Icon class="w-4 h-4 mr-2" />
              <span class="font-bold">Show Input Data (Criteria & Comments)</span>
              <span v-if="result.generation_started_at" class="ml-2 text-xs font-bold text-gray-600">
                - Generated: {{ formatTimestamp(result.generation_started_at) }}
              </span>
            </span>
            <ChevronDownIcon
              class="w-5 h-5 transition-transform"
              :class="{ 'rotate-180': result.showInputData }"
            />
          </button>
          
          <div v-show="result.showInputData" class="mt-2 p-4 bg-gray-50 rounded border border-gray-200 space-y-4">
            <!-- Comment List -->
            <div v-if="formatCommentList(result).length > 0">
              <h4 class="text-sm font-bold text-gray-700 mb-2">Comment List:</h4>
              <div class="bg-white p-3 rounded border border-gray-300 text-xs font-mono space-y-1">
                <div v-for="(comment, idx) in formatCommentList(result)" :key="idx" class="text-gray-800">
                  {{ comment }}
                </div>
              </div>
            </div>

            <!-- User Criteria Value Table -->
            <div v-if="formatUserCriteriaTable(result).length > 0">
              <h4 class="text-sm font-bold text-gray-700 mb-2">User Criteria Value Table (only user data with role average)</h4>
              <div class="bg-white p-3 rounded border border-gray-300 overflow-x-auto">
                <table class="w-full text-xs font-mono">
                  <thead>
                    <tr class="border-b border-gray-300">
                      <th class="text-left py-1 pr-4">Criterion</th>
                      <th class="text-center py-1 px-2">Value</th>
                      <th class="text-center py-1 pl-2">Average</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="(row, idx) in formatUserCriteriaTable(result)" :key="idx" class="border-t border-gray-200">
                      <td class="py-1 pr-4">{{ row.name }}</td>
                      <td class="text-center py-1 px-2" :class="{
                        'text-green-700 font-semibold': row.value >= row.avg,
                        'text-orange-700 font-semibold': row.value < row.avg
                      }">{{ row.value }}</td>
                      <td class="text-center py-1 pl-2 text-gray-600">{{ row.avg }}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <!-- Team Criteria Value Table -->
            <div v-if="formatTeamCriteriaTable(result).length > 0">
              <h4 class="text-sm font-bold text-gray-700 mb-2">Team Criteria Value Table (collective view of user's teams)</h4>
              <div class="bg-white p-3 rounded border border-gray-300 overflow-x-auto">
                <table class="w-full text-xs font-mono">
                  <thead>
                    <tr class="border-b border-gray-300">
                      <th class="text-left py-1 pr-4">Criterion</th>
                      <th class="text-center py-1 px-2">Value</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="(row, idx) in formatTeamCriteriaTable(result)" :key="idx" class="border-t border-gray-200">
                      <td class="py-1 pr-4">{{ row.name }}</td>
                      <td class="text-center py-1 px-2 text-gray-800">{{ row.value }}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <div v-if="formatCommentList(result).length === 0 && formatUserCriteriaTable(result).length === 0 && formatTeamCriteriaTable(result).length === 0" class="text-center py-4 text-gray-500 text-sm">
              No input data available
            </div>
          </div>
        </div>

        <!-- Individual Feedback -->
        <div v-if="result.status !== 'error'" class="mb-3">
          <label class="block font-semibold text-gray-700 mb-1 text-sm">Individual Feedback</label>
          <textarea
            v-if="result.isEditing"
            v-model="result.individual_feedback"
            class="w-full bg-white p-3 rounded border border-blue-300 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 min-h-[120px]"
            rows="6"
          ></textarea>
          <div v-else class="bg-white p-3 rounded border border-gray-200 text-sm text-gray-700 whitespace-pre-wrap">
            <span v-html="renderMarkdownBold(result.individual_feedback)"></span>
          </div>
        </div>

        <!-- Team Feedback -->
        <div v-if="result.status !== 'error' && (result.team_feedback || result.isEditing)" class="mb-3">
          <label class="block font-semibold text-gray-700 mb-1 text-sm flex items-center">
            <UserGroupIcon class="w-4 h-4 mr-1" />
            Team Feedback
          </label>
          <textarea
            v-if="result.isEditing"
            v-model="result.team_feedback"
            class="w-full bg-green-50 p-3 rounded border border-green-300 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-green-500 min-h-[80px]"
            rows="4"
            placeholder="Team-specific feedback (optional)"
          ></textarea>
          <div v-else class="bg-green-50 p-3 rounded border border-green-200 text-sm text-gray-700 whitespace-pre-wrap">
            <span class="font-semibold text-green-800">TEAM:</span>&nbsp;<span v-html="renderMarkdownBold(result.team_feedback)"></span>
          </div>
        </div>

        <!-- Recommendation -->
        <div v-if="result.status !== 'error'">
          <label class="block font-semibold text-gray-700 mb-1 text-sm">Recommendation</label>
          <textarea
            v-if="result.isEditing"
            v-model="result.recommendation"
            class="w-full bg-white p-3 rounded border border-blue-300 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 min-h-[120px]"
            rows="6"
          ></textarea>
          <div v-else class="bg-white p-3 rounded border border-gray-200 text-sm text-gray-700 whitespace-pre-wrap">
            <span v-html="renderMarkdownBold(result.recommendation)"></span>
          </div>
        </div>

        <!-- Approval Status -->
        <div v-if="result.approved !== null" class="mt-3 pt-3 border-t border-gray-300">
          <p class="text-sm font-semibold" :class="{
            'text-green-700': result.approved === true,
            'text-red-700': result.approved === false
          }">
            {{ result.approved ? '✓ Approved and Saved' : '✗ Declined' }}
          </p>
        </div>
      </div>
    </div>

    <!-- Loading State -->
    <div v-else-if="isLoadingSuggestions" class="text-center p-12 bg-gray-50 rounded-lg">
      <ArrowPathIcon class="animate-spin h-16 w-16 mx-auto text-indigo-600 mb-4" />
      <h3 class="text-lg font-semibold text-gray-700 mb-2">Loading Suggestions...</h3>
      <p class="text-sm text-gray-500">Checking for saved AI feedback suggestions</p>
    </div>

    <!-- Empty State -->
    <div v-else-if="!isGenerating" class="text-center p-12 bg-gray-50 rounded-lg">
      <SparklesIcon class="w-16 h-16 mx-auto text-gray-400 mb-4" />
      <h3 class="text-lg font-semibold text-gray-700 mb-2">No Feedback Generated Yet</h3>
      <p class="text-sm text-gray-500">Select a session and click "Generate for All Users" to begin</p>
    </div>

    <!-- Confirm Modal -->
    <ConfirmModal
      :isOpen="showConfirmModal"
      :title="confirmModalConfig.title"
      :message="confirmModalConfig.message"
      @confirm="handleConfirmAction"
      @cancel="showConfirmModal = false"
    />

    <!-- Approve Confirm Modal -->
    <ApproveConfirmModal
      :isOpen="showApproveConfirmModal"
      :title="approveConfirmModalConfig.title"
      :message="approveConfirmModalConfig.message"
      :confirmLabel="approveConfirmModalConfig.confirmLabel"
      @confirm="handleApproveConfirmAction"
      @cancel="showApproveConfirmModal = false"
    />
  </div>
</template>

<script>
import BaseSelect from "@/BaseComponents/BaseSelect.vue";
import BaseButton from "@/BaseComponents/BaseButton.vue";
import BaseInfoIcon from "@/BaseComponents/BaseInfoIcon.vue";
import { ERKLAERUNG } from "@/async-sessions/erklaerungen";
import { SparklesIcon, UserGroupIcon, Squares2X2Icon, ChevronDownIcon } from '@heroicons/vue/24/outline';
import { ArrowPathIcon } from '@heroicons/vue/24/solid';
import ConfirmModal from "@/BaseComponents/ConfirmModal.vue";
import ApproveConfirmModal from "@/BaseComponents/ApproveConfirmModal.vue";
import { getLiveSessions } from "@/live-sessions/api/sessions";
import { systemSettingsApi } from "@/api/systemSettings";
import {
  generateBulkFeedback as apiBulkFeedback,
  getPendingFeedback,
  approveFeedback as apiApproveFeedback,
  clearAllPendingFeedback,
  clearIndividualFeedback
} from "@/live-sessions/api/aiFeedback";
import { useServerSentEvents } from "@/composables/useServerSentEvents";
import { useAuth } from "@/stores/auth";

export default {
  name: "AIFeedback",
  components: { BaseSelect, BaseButton, BaseInfoIcon, ConfirmModal, ApproveConfirmModal, SparklesIcon, UserGroupIcon, Squares2X2Icon, ChevronDownIcon, ArrowPathIcon },
  setup() {
    const auth = useAuth();
    return { auth };
  },
  data() {
    return {
      ERKLAERUNG,
      sessions: [],
      selectedSessionId: "",
      isGenerating: false,
      isLoadingSuggestions: false,
      generationResult: null,
      feedbackResults: [],
      showConfirmModal: false,
      confirmModalConfig: {
        title: '',
        message: '',
        action: null,
        actionData: null
      },
      showApproveConfirmModal: false,
      approveConfirmModalConfig: {
        title: '',
        message: '',
        confirmLabel: 'Approve',
        action: null,
        actionData: null
      },
      totalUsersToProcess: 0,
      sseConnection: null, // Store SSE connection
      generatingForSessionId: null, // Session ID currently being generated
      historicalAvgTimePerUserMs: null, // Average time per user from prior generations
      historicalAvgTimePerUserLlmMs: null,
      historicalAvgTimePerUserRagMs: null,
      currentLlmModel: null,
    };
  },
  computed: {
    isGeneratingForDifferentSession() {
      const result = this.isGenerating && this.generatingForSessionId && String(this.selectedSessionId) !== String(this.generatingForSessionId);
      console.log('[AIFeedback] isGeneratingForDifferentSession:', {
        isGenerating: this.isGenerating,
        generatingForSessionId: this.generatingForSessionId,
        selectedSessionId: this.selectedSessionId,
        result
      });
      return result;
    },
    estimatedTimeRemaining() {
      if (!this.isGenerating || this.isGeneratingForDifferentSession) {
        return null;
      }

      const usersCompleted = this.feedbackResults.length;
      if (usersCompleted === 0 && !this.historicalAvgTimePerUserMs) {
        return null;
      }
      const usersRemaining = this.totalUsersToProcess - usersCompleted;

      if (usersRemaining <= 0) {
        return null;
      }

      if (!this.historicalAvgTimePerUserMs) {
        return null;
      }

      const averageTimePerUser = this.historicalAvgTimePerUserMs;

      // Estimate remaining time in seconds
      const estimatedMs = averageTimePerUser * usersRemaining;
      const estimatedSeconds = Math.ceil(estimatedMs / 1000);

      return this.formatTime(estimatedSeconds);
    },
    estimatedTimePerUserLlm() {
      if (!this.historicalAvgTimePerUserLlmMs) {
        return null;
      }

      const estimatedSeconds = Math.ceil(this.historicalAvgTimePerUserLlmMs / 1000);
      return this.formatTime(estimatedSeconds);
    },
    estimatedTimePerUserRag() {
      if (!this.historicalAvgTimePerUserRagMs) {
        return null;
      }

      const estimatedSeconds = Math.ceil(this.historicalAvgTimePerUserRagMs / 1000);
      return this.formatTime(estimatedSeconds);
    },
    sessionOptions() {
      const flatten = (sessions, depth = 0) => {
        return sessions.flatMap(s => [
          { value: s.id.toString(), label: `${'— '.repeat(depth)}${s.title}` },
          ...(s.children?.length ? flatten(s.children, depth + 1) : []),
        ]);
      };
      return flatten(this.sessions);
    }
  },
  methods: {
    renderMarkdownBold(text) {
      if (!text || typeof text !== 'string') {
        return text;
      }
      return text.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
    },
    formatTime(seconds) {
      if (seconds < 60) {
        return `${seconds} Sek.`;
      } else if (seconds < 3600) {
        const minutes = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return secs > 0 ? `${minutes} Min. ${secs} Sek.` : `${minutes} Min.`;
      } else {
        const hours = Math.floor(seconds / 3600);
        const minutes = Math.floor((seconds % 3600) / 60);
        return minutes > 0 ? `${hours} Std. ${minutes} Min.` : `${hours} Std.`;
      }
    },
    async fetchSessions() {
      try {
        const res = await getLiveSessions();
        this.sessions = res.data;
      } catch (err) {
        console.error("Failed to fetch sessions:", err);
        this.sessions = [];
      }
    },
    async loadCurrentLlmModel() {
      try {
        const response = await systemSettingsApi.getLlmModels();
        this.currentLlmModel = response.data.configured_model || null;
      } catch (err) {
        this.currentLlmModel = null;
      }
    },
    handleLlmModelUpdated(event) {
      if (event && event.detail && event.detail.model) {
        this.currentLlmModel = event.detail.model;
      } else {
        this.loadCurrentLlmModel();
      }
    },
    async loadAiFeedbackSuggestions() {
      if (!this.selectedSessionId) {
        this.feedbackResults = [];
        this.generationResult = null;
        this.historicalAvgTimePerUserMs = null;
        this.historicalAvgTimePerUserLlmMs = null;
        this.historicalAvgTimePerUserRagMs = null;
        return;
      }

      this.isLoadingSuggestions = true;
      console.log(`Loading AI feedback suggestions for session ${this.selectedSessionId}...`);

      try {
        const response = await getPendingFeedback(this.selectedSessionId);
        const result = response.data;
        this.historicalAvgTimePerUserMs = result.avg_time_per_user_ms || null;
        this.historicalAvgTimePerUserLlmMs = result.avg_time_per_user_ms_llm || null;
        this.historicalAvgTimePerUserRagMs = result.avg_time_per_user_ms_rag || null;
        this.currentLlmModel = result.model_used || this.currentLlmModel;
        
        console.log(`Loaded ${result.results?.length || 0} suggestions from database`);
        
        if (result.results && result.results.length > 0) {
          this.generationResult = result;
          this.feedbackResults = result.results.map(r => {
            // Split evaluation into individual and team feedback
            let individual_feedback = r.evaluation || '';
            let team_feedback = '';
            
            // Check if evaluation contains **TEAM:** section
            const teamMatch = individual_feedback.match(/\n\n\*\*TEAM:\*\*\s*(.+)$/s);
            if (teamMatch) {
              team_feedback = teamMatch[1].trim();
              individual_feedback = individual_feedback.substring(0, teamMatch.index).trim();
            }
            
            return {
              ...r,
              individual_feedback,
              team_feedback,
              approved: r.approved !== undefined ? r.approved : null,
              isProcessing: false,
              isEditing: false,
              showInputData: false, // Track expansion state
              originalIndividualFeedback: individual_feedback,
              originalTeamFeedback: team_feedback,
              originalRecommendation: r.recommendation
            };
          });
          
          this.totalUsersToProcess = result.total_users;
        } else {
          this.feedbackResults = [];
          this.generationResult = null;
          this.totalUsersToProcess = result.total_users || 0;
        }
        
        // Check if generation is still in progress based on explicit backend flag
        if (result.is_generating) {
          console.log(`Generation in progress detected: ${result.results?.length || 0} / ${result.total_users} users`);
          console.log('[AIFeedback] loadAiFeedbackSuggestions - Setting generatingForSessionId:', this.selectedSessionId);
          this.isGenerating = true;
          this.generatingForSessionId = this.selectedSessionId;
          // Reconnect to SSE stream
          this.connectToSSE();
        } else {
          // Only clear generation state if loading the session that's currently generating
          // Don't clear if we're generating for a different session
          if (!this.generatingForSessionId || String(this.generatingForSessionId) === String(this.selectedSessionId)) {
            console.log('[AIFeedback] loadAiFeedbackSuggestions - Clearing generatingForSessionId');
            this.isGenerating = false;
            this.generatingForSessionId = null;
          } else {
            console.log('[AIFeedback] loadAiFeedbackSuggestions - Preserving generatingForSessionId for different session');
          }
        }
        
      } catch (err) {
        console.error("Failed to load AI feedback suggestions:", err);
        this.feedbackResults = [];
        this.generationResult = null;
      } finally {
        this.isLoadingSuggestions = false;
      }
    },
    async generateBulkFeedback(useRag = false) {
      if (!this.selectedSessionId) return;

      console.log('[AIFeedback] generateBulkFeedback - Setting generatingForSessionId:', this.selectedSessionId);
      this.isGenerating = true;
      this.generatingForSessionId = this.selectedSessionId;
      this.feedbackResults = [];
      this.generationResult = null;
      this.totalUsersToProcess = 0;
      this.historicalAvgTimePerUserMs = null;
      this.historicalAvgTimePerUserLlmMs = null;
      this.historicalAvgTimePerUserRagMs = null;

      try {
        // Start async generation - returns immediately
        const response = await apiBulkFeedback(parseInt(this.selectedSessionId), useRag);
        const data = response.data;
        this.historicalAvgTimePerUserMs = data.avg_time_per_user_ms || null;
        this.historicalAvgTimePerUserLlmMs = data.avg_time_per_user_ms_llm || null;
        this.historicalAvgTimePerUserRagMs = data.avg_time_per_user_ms_rag || null;
        this.currentLlmModel = data.model_used || this.currentLlmModel;
        
        this.totalUsersToProcess = data.total_users || 0;
        
        console.log(`Started background generation for session ${this.selectedSessionId}, total users: ${this.totalUsersToProcess}`);
        
        // Connect to SSE stream for real-time updates
        this.connectToSSE();
        
      } catch (err) {
        console.error("Failed to start bulk feedback generation:", err);
        alert(`Failed to start feedback generation: ${err.message}`);
        this.isGenerating = false;
        this.generatingForSessionId = null;
      }
    },
    
    connectToSSE() {
      // Disconnect any existing connection
      if (this.sseConnection) {
        this.sseConnection.disconnect(true);
      }

      // Use the same API base URL as axios
      const apiBaseUrl = process.env.VUE_APP_API_URL || '/api';
      
      // Add authentication token as query parameter (EventSource doesn't support custom headers)
      const token = this.auth.token.value;
      const sseUrl = `${apiBaseUrl}/ai-feedback/stream/${this.selectedSessionId}${token ? `?token=${token}` : ''}`;

      this.sseConnection = useServerSentEvents(sseUrl, {
        connected: (data) => {
          console.log('SSE connected:', data);
        },
        progress: async (data) => {
          console.log('SSE progress event:', data);
          await this.loadAiFeedbackSuggestions();
        },
        complete: async (data) => {
          console.log('SSE complete event:', data);
          // Final reload and cleanup
          await this.loadAiFeedbackSuggestions();
          console.log('[AIFeedback] SSE complete - Clearing generatingForSessionId');
          this.isGenerating = false;
          this.generatingForSessionId = null;
          if (this.sseConnection) {
            this.sseConnection.disconnect(true);
            this.sseConnection = null;
          }
        },
        error: (data) => {
          console.error('SSE error event:', data);
          console.log('[AIFeedback] SSE error - Clearing generatingForSessionId');
          this.isGenerating = false;
          this.generatingForSessionId = null;
        }
      });

      this.sseConnection.connect();
    },
    
    async checkForActiveGeneration() {
      // Check all sessions to find if any is currently generating
      console.log('[AIFeedback] checkForActiveGeneration - Checking all sessions');
      const allSessionIds = this.sessionOptions.map(opt => opt.value);
      
      for (const sessionId of allSessionIds) {
        try {
          const response = await getPendingFeedback(sessionId);
          const result = response.data;
          
          if (result.is_generating) {
            console.log(`[AIFeedback] Found active generation for session ${sessionId}`);
            this.isGenerating = true;
            this.generatingForSessionId = sessionId;
            
            // Only connect to SSE if this is the currently selected session
            if (String(sessionId) === String(this.selectedSessionId)) {
              this.connectToSSE();
            }
            
            return; // Stop after finding the first generating session
          }
        } catch (err) {
          console.error(`Failed to check session ${sessionId}:`, err);
        }
      }
      
      console.log('[AIFeedback] No active generation found');
    },
    
    disconnectSSE() {
      if (this.sseConnection) {
        this.sseConnection.disconnect(true);
        this.sseConnection = null;
      }
    },
    async approveFeedback(index) {
      const result = this.feedbackResults[index];
      if (result.status === 'error') return;

      // Show confirmation modal
      this.approveConfirmModalConfig = {
        title: 'Approve Feedback',
        message: `Are you sure you want to approve the AI-generated feedback for ${result.user_name}? This will save the evaluation and recommendation to the user session.`,
        confirmLabel: 'Approve',
        action: 'approveSingle',
        actionData: index
      };
      this.showApproveConfirmModal = true;
    },
    async executeApproveSingle(index) {
      const result = this.feedbackResults[index];
      if (result.status === 'error') return;

      result.isProcessing = true;

      try {
        // Combine individual and team feedback into evaluation
        let evaluation = result.individual_feedback || '';
        if (result.team_feedback && result.team_feedback.trim()) {
          evaluation += `\n\n**TEAM:** ${result.team_feedback.trim()}`;
        }
        
        await apiApproveFeedback(this.selectedSessionId, {
          user_id: result.user_id,
          approved: true,
          evaluation: evaluation,
          recommendation: result.recommendation,
          user_history: result.user_history,
          team_history: result.team_history
        });

        result.approved = true;
        
      } catch (err) {
        console.error("Failed to approve feedback:", err);
        alert(`Failed to approve feedback: ${err.message}`);
      } finally {
        result.isProcessing = false;
      }
    },
    async declineFeedback(index) {
      const result = this.feedbackResults[index];
      if (result.status === 'error') return;

      // Show confirmation modal
      this.approveConfirmModalConfig = {
        title: 'Decline Feedback',
        message: `Are you sure you want to decline the AI-generated feedback for ${result.user_name}? This will mark it as declined without saving it to the user session.`,
        confirmLabel: 'Decline',
        action: 'declineSingle',
        actionData: index
      };
      this.showApproveConfirmModal = true;
    },
    async executeDeclineSingle(index) {
      const result = this.feedbackResults[index];
      if (result.status === 'error') return;

      result.isProcessing = true;

      try {
        // Combine individual and team feedback into evaluation
        let evaluation = result.individual_feedback || '';
        if (result.team_feedback && result.team_feedback.trim()) {
          evaluation += `\n\n**TEAM:** ${result.team_feedback.trim()}`;
        }
        
        await apiApproveFeedback(this.selectedSessionId, {
          user_id: result.user_id,
          approved: false,
          evaluation: evaluation,
          recommendation: result.recommendation,
          user_history: result.user_history,
          team_history: result.team_history
        });

        result.approved = false;
        
      } catch (err) {
        console.error("Failed to decline feedback:", err);
        alert(`Failed to decline feedback: ${err.message}`);
      } finally {
        result.isProcessing = false;
      }
    },
    async approveAll() {
      const pendingCount = this.feedbackResults.filter(
        r => r.approved === null && r.status !== 'error'
      ).length;
      
      if (pendingCount === 0) {
        alert('No pending feedbacks to approve.');
        return;
      }

      // Show confirmation modal
      this.approveConfirmModalConfig = {
        title: 'Approve All Feedbacks',
        message: `Are you sure you want to approve all ${pendingCount} pending AI-generated feedbacks? This will save them to their respective user sessions.`,
        confirmLabel: 'Approve All',
        action: 'approveAll',
        actionData: null
      };
      this.showApproveConfirmModal = true;
    },
    async executeApproveAll() {
      for (let i = 0; i < this.feedbackResults.length; i++) {
        if (this.feedbackResults[i].approved === null && this.feedbackResults[i].status !== 'error') {
          await this.executeApproveSingle(i);
        }
      }
    },
    async declineAll() {
      const pendingCount = this.feedbackResults.filter(
        r => r.approved === null && r.status !== 'error'
      ).length;
      
      if (pendingCount === 0) {
        alert('No pending feedbacks to decline.');
        return;
      }

      // Show confirmation modal
      this.approveConfirmModalConfig = {
        title: 'Decline All Feedbacks',
        message: `Are you sure you want to decline all ${pendingCount} pending AI-generated feedbacks? They will be marked as declined without being saved to user sessions.`,
        confirmLabel: 'Decline All',
        action: 'declineAll',
        actionData: null
      };
      this.showApproveConfirmModal = true;
    },
    async executeDeclineAll() {
      for (let i = 0; i < this.feedbackResults.length; i++) {
        if (this.feedbackResults[i].approved === null && this.feedbackResults[i].status !== 'error') {
          await this.executeDeclineSingle(i);
        }
      }
    },
    async clearAllSuggestions() {
      if (!this.selectedSessionId) return;
      
      this.confirmModalConfig = {
        title: 'Clear All Suggestions',
        message: 'Are you sure you want to clear all AI feedback suggestions? This cannot be undone.',
        action: 'clearAll',
        actionData: null
      };
      this.showConfirmModal = true;
    },
    async executeClearAll() {
      if (!this.selectedSessionId) return;

      try {
        await clearAllPendingFeedback(this.selectedSessionId);

        this.feedbackResults = [];
        this.generationResult = null;
        
      } catch (err) {
        console.error("Failed to clear AI feedback suggestions:", err);
        alert(`Failed to clear feedback: ${err.message}`);
      }
    },

    async clearIndividualSuggestion(index) {
      const result = this.feedbackResults[index];
      if (!this.selectedSessionId || !result) return;
      
      this.confirmModalConfig = {
        title: 'Clear Suggestion',
        message: `Are you sure you want to clear the suggestion for ${result.user_name}?`,
        action: 'clearIndividual',
        actionData: index
      };
      this.showConfirmModal = true;
    },
    async executeClearIndividual(index) {
      const result = this.feedbackResults[index];
      if (!this.selectedSessionId || !result) return;

      try {
        await clearIndividualFeedback(this.selectedSessionId, result.user_id);

        this.feedbackResults.splice(index, 1);
        
      } catch (err) {
        console.error("Failed to clear individual suggestion:", err);
        alert(`Failed to clear suggestion: ${err.message}`);
      }
    },
    toggleEdit(index) {
      const result = this.feedbackResults[index];
      result.isEditing = true;
    },
    saveEdit(index) {
      const result = this.feedbackResults[index];
      result.isEditing = false;
      result.originalIndividualFeedback = result.individual_feedback;
      result.originalTeamFeedback = result.team_feedback;
      result.originalRecommendation = result.recommendation;
    },
    cancelEdit(index) {
      const result = this.feedbackResults[index];
      result.individual_feedback = result.originalIndividualFeedback;
      result.team_feedback = result.originalTeamFeedback;
      result.recommendation = result.originalRecommendation;
      result.isEditing = false;
    },
    handleConfirmAction() {
      this.showConfirmModal = false;
      
      if (this.confirmModalConfig.action === 'clearAll') {
        this.executeClearAll();
      } else if (this.confirmModalConfig.action === 'clearIndividual') {
        this.executeClearIndividual(this.confirmModalConfig.actionData);
      }
    },
    handleApproveConfirmAction() {
      this.showApproveConfirmModal = false;
      
      if (this.approveConfirmModalConfig.action === 'approveSingle') {
        this.executeApproveSingle(this.approveConfirmModalConfig.actionData);
      } else if (this.approveConfirmModalConfig.action === 'declineSingle') {
        this.executeDeclineSingle(this.approveConfirmModalConfig.actionData);
      } else if (this.approveConfirmModalConfig.action === 'approveAll') {
        this.executeApproveAll();
      } else if (this.approveConfirmModalConfig.action === 'declineAll') {
        this.executeDeclineAll();
      }
    },
    
    // Format comment list exactly as in the prompt
    formatCommentList(result) {
      const comments = [];
      
      // User history comments
      if (result.user_history) {
        for (const entry of result.user_history) {
          if (entry.text_values && entry.text_values.length > 0) {
            for (const textItem of entry.text_values) {
              // Handle both old format (string) and new format (object with text and value_change)
              let text = '';
              let valueChange = null;
              
              if (typeof textItem === 'string') {
                text = textItem;
              } else if (typeof textItem === 'object' && textItem.text) {
                text = textItem.text;
                valueChange = textItem.value_change;
              }
              
              // Format with value change if available
              if (valueChange !== null && valueChange !== 0) {
                const sign = valueChange > 0 ? '+' : '';
                comments.push(`- ${entry.criterion_name} (${sign}${valueChange}): ${text}`);
              } else {
                comments.push(`- ${entry.criterion_name}: ${text}`);
              }
            }
          }
        }
      }
      
      // Team history comments (should be empty as merged, but keep for safety)
      if (result.team_history) {
        for (const entry of result.team_history) {
          if (entry.text_values && entry.text_values.length > 0) {
            for (const textItem of entry.text_values) {
              // Handle both old format (string) and new format (object with text and value_change)
              let text = '';
              let valueChange = null;
              
              if (typeof textItem === 'string') {
                text = textItem;
              } else if (typeof textItem === 'object' && textItem.text) {
                text = textItem.text;
                valueChange = textItem.value_change;
              }
              
              const prefix = text.includes('TEAM:') ? '' : 'TEAM: ';
              
              // Format with value change if available
              if (valueChange !== null && valueChange !== 0) {
                const sign = valueChange > 0 ? '+' : '';
                comments.push(`- ${entry.criterion_name} (${sign}${valueChange}): ${prefix}${text}`);
              } else {
                const finalPrefix = text.includes('TEAM:') ? '' : 'TEAM: ';
                comments.push(`- ${entry.criterion_name}: ${finalPrefix}${text}`);
              }
            }
          }
        }
      }
      
      return comments;
    },
    
    // Format USER criteria table (only user data with role average)
    formatUserCriteriaTable(result) {
      const rows = [];
      
      if (result.user_history) {
        for (const entry of result.user_history) {
          if (entry.criterion_type === 'countable') {
            rows.push({
              name: entry.criterion_name,
              value: entry.int_value || 0,
              avg: entry.avg_value ? (typeof entry.avg_value === 'number' ? entry.avg_value.toFixed(1) : entry.avg_value) : '0.0'
            });
          }
        }
      }
      
      return rows;
    },
    
    // Format TEAM criteria table (only team data without average)
    formatTeamCriteriaTable(result) {
      const rows = [];
      
      if (result.team_history) {
        for (const entry of result.team_history) {
          if (entry.criterion_type === 'countable' && entry.int_value > 0) {
            rows.push({
              name: entry.criterion_name,
              value: entry.int_value || 0
            });
          }
        }
      }
      
      return rows;
    },
    
    // Format timestamp to German locale
    formatTimestamp(isoString) {
      if (!isoString) return '';
      try {
        const date = new Date(isoString);
        return date.toLocaleString('de-DE', {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit'
        });
      } catch (e) {
        return isoString;
      }
    }
  },
  watch: {
    selectedSessionId(newVal, oldVal) {
      console.log('[AIFeedback] selectedSessionId changed:', { oldVal, newVal, generatingForSessionId: this.generatingForSessionId, isGenerating: this.isGenerating });
      // Disconnect SSE when switching sessions, but preserve generation state
      if (oldVal && oldVal !== newVal) {
        // Only disconnect and clear if NOT generating OR if switching TO the generating session
        if (!this.generatingForSessionId || String(newVal) === String(this.generatingForSessionId)) {
          console.log('[AIFeedback] watch - Disconnecting SSE and clearing state');
          this.disconnectSSE();
          this.isGenerating = false;
          this.generatingForSessionId = null;
        } else {
          console.log('[AIFeedback] watch - Preserving generation state (generating for different session)');
        }
      }
      
      if (newVal) {
        if (this.$route.query.session !== newVal) {
          this.$router.push({ query: { session: newVal } });
        }
        this.loadAiFeedbackSuggestions();
      } else {
        if (this.$route.query.session) {
          this.$router.push({ query: {} });
        }
        this.feedbackResults = [];
        this.generationResult = null;
      }
    },
    '$route.query.session'(newVal) {
      if (newVal !== this.selectedSessionId) {
        this.selectedSessionId = newVal || '';
      }
    }
  },
  async mounted() {
    await this.fetchSessions();
    await this.loadCurrentLlmModel();
    window.addEventListener('llm-model-updated', this.handleLlmModelUpdated);
    
    const sessionFromUrl = this.$route.query.session;
    if (sessionFromUrl) {
      this.selectedSessionId = sessionFromUrl;
    }
    
    if (this.selectedSessionId) {
      await this.loadAiFeedbackSuggestions();
    }
    
    // Check if any session is currently generating after loading
    await this.checkForActiveGeneration();
  },
  beforeUnmount() {
    // Clean up SSE connection when component is destroyed
    this.disconnectSSE();
    window.removeEventListener('llm-model-updated', this.handleLlmModelUpdated);
  }
};
</script>
